import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  Typography,
  InputAdornment,
  IconButton,
  Divider,
  Box,
  CircularProgress,
  Radio,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TextIcon from '@mui/icons-material/TextFields';
import FileIcon from '@mui/icons-material/FilePresent';
import { useLang } from '../../contexts/langContext';
import { useMaterials } from '../../hooks/useMaterials';
import DoneIcon from '@mui/icons-material/Done';
import type { MaterialWithFileMetadata } from '../../types';

interface MaterialPickerProps {
  onSelect: (materials: MaterialWithFileMetadata[]) => void;
  children: (openDialog: () => void) => React.ReactNode;
  multiple?: boolean;
  projectId?: string;
  title?: string;
  /** Pre-selected fileServiceFileId values */
  initialSelected?: string[];
  hasOutline?: boolean;
  accept?: string[];
  lang?: 'en' | 'zh-CN' | 'zh-HK';
}

function MaterialPicker({
  onSelect,
  children,
  multiple = false,
  projectId,
  title,
  initialSelected = [],
  hasOutline,
  accept,
  lang = 'en',
}: MaterialPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(initialSelected);
  const [localSearchQuery, setLocalSearchQuery] = useState('');

  const {
    materials,
    isLoading,
    fetchMaterials,
    searchQuery,
    setSearchQuery,
    handleSearch,
  } = useMaterials({ projectId, initialFetch: isOpen, hasOutline });

  // Apply accept filter if provided
  const displayedMaterials = accept && accept.length > 0
    ? materials.filter((m) => {
        const type = m.fileMetadata?.type || '';
        return accept.some((allowed) => type.includes(allowed));
      })
    : materials;

  useEffect(() => {
    setSelected(initialSelected);
  }, [initialSelected]);

  const handleToggle = (fileServiceFileId: string) => () => {
    if (multiple) {
      const currentIndex = selected.indexOf(fileServiceFileId);
      const newSelected = [...selected];

      if (currentIndex === -1) {
        newSelected.push(fileServiceFileId);
      } else {
        newSelected.splice(currentIndex, 1);
      }

      setSelected(newSelected);
    } else {
      setSelected([fileServiceFileId]);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelected(initialSelected);
    setLocalSearchQuery('');
  };

  const handleConfirm = () => {
    const selectedMaterials = displayedMaterials.filter(material =>
      selected.includes(material.fileServiceFileId)
    );
    onSelect(selectedMaterials);
    handleClose();
  };

  const handleLocalSearch = () => {
    setSearchQuery(localSearchQuery);
    handleSearch();
  };

  const getIcon = (type: string) => {
    if (type.includes('text')) return <TextIcon />;
    return <FileIcon />;
  };

  const dialogTitle = title || (lang === 'en' ? 'Select Material' : '选择材料');

  return (
    <>
      {children(() => setIsOpen(true))}
      
      <Dialog 
        open={isOpen} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
      >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          placeholder={lang === 'en' ? 'Search materials...' : '搜索材料...'}
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleLocalSearch()}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleLocalSearch} disabled={isLoading}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : displayedMaterials.length > 0 ? (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {displayedMaterials.map((material, index) => {
              return (
                <div key={material._id}>
                  <ListItem
                    dense
                    button
                    onClick={handleToggle(material.fileServiceFileId)}
                  >
                    <ListItemIcon>
                      {getIcon(material.fileMetadata?.type || '')}
                    </ListItemIcon>
                    <ListItemText
                      primary={material.fileMetadata?.filename || 'Untitled'}
                      secondary={new Date(material.createdAt).toLocaleDateString()} />
                    <ListItemSecondaryAction>
                      {multiple ? (
                        <Checkbox
                          edge="end"
                          checked={selected.includes(material.fileServiceFileId)}
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(material.fileServiceFileId)();
                          }}
                          inputProps={{ 'aria-labelledby': `material-${material._id}` }}
                        />
                      ) : (
                        <Radio
                          edge="end"
                          checked={selected.includes(material.fileServiceFileId)}
                          tabIndex={-1}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(material.fileServiceFileId)();
                          }}
                          inputProps={{ 'aria-labelledby': `material-${material._id}` }}
                        />
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < displayedMaterials.length - 1 && <Divider />}
                </div>
              );
            })}
          </List>
        ) : (
          <Box p={3} textAlign="center">
            <Typography color="textSecondary">
              {lang === 'en' ? 'No materials found' : '未找到材料'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {lang === 'en' ? 'Cancel' : lang === 'zh-HK' ? '取消' : '取消'}
        </Button>
        <Button 
          onClick={handleConfirm} 
          color="primary" 
          disabled={selected.length === 0}
        >
          {lang === 'en' ? 'Select' : lang === 'zh-HK' ? '选择' : '选择'}
        </Button>
      </DialogActions>
      </Dialog>
    </>
  );
}

export default MaterialPicker;
